import { DOCUMENT, Inject, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { MasterService } from './common/master.service';
import { IApiResponse } from '../interface/api-response.interface';
import { Global } from '../data/global';
import { BaseUrlEnum, getBaseURL } from './common/base-url-resolver.util';
import { Meta, Title } from '@angular/platform-browser';

export interface ISeoConfig {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  robots?: string;
  type?: 'website' | 'article' | 'product';
  /** Canonical path when this page duplicates another URL (defaults to the current path) */
  canonicalPath?: string;
}

// Per-tenant values live in Global
export const SITE_NAME = Global.SiteName;
export const DEFAULT_DESCRIPTION = Global.SiteDescription;

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private masterService = inject(MasterService);
  public skeletonLoader: boolean = false;
  private platformId = inject<Object>(PLATFORM_ID);
  private document = inject(DOCUMENT);
  private meta = inject(Meta);
  private title = inject(Title);
  public readonly InventoryImagePrefix = 'https://morzaa-inv-images-prod.s3.ap-southeast-1.amazonaws.com/'

  private getCurrentOrigin(): string {
    const docLocationOrigin = this.document?.location?.origin;
    if (docLocationOrigin) {
      return docLocationOrigin;
    }

    const requestOrigin = (globalThis as any).__REQUEST_ORIGIN__ as string | undefined;
    if (requestOrigin) {
      return requestOrigin;
    }

    return 'http://localhost:4200';
  }

  // SSR-fetched SEO data is reused in the browser (TransferState) instead of being requested twice
  getSeoData(pageId : number, pageType: number, languageId?: number) : Observable<IApiResponse> {
    return this.masterService.getCached<IApiResponse>(
      `seo-cms-${pageType}-${pageId}`,
      getBaseURL(this.platformId,BaseUrlEnum.CMS),
      `seo/GetSeoData?clientId=${Global.ClientID}&pageId=${pageId}&Pagetype=${pageType}&languageId=${Global.LanguageID}`,
    );
  }

  getSeoDataFromInventory(pageId: number, pageType: number): Observable<IApiResponse> {
    return this.masterService.getCached<IApiResponse>(
      `seo-inventory-${pageType}-${pageId}`,
      getBaseURL(this.platformId,BaseUrlEnum.Inventory),
      `ecommerce/seo?pageId=${pageId}&Pagetype=${pageType}`,
    );
  }

  /** "<page> | <site name>" */
  pageTitle(page: string): string {
    return page ? `${page} | ${SITE_NAME}` : SITE_NAME;
  }

  /**
   * SEO from the CMS (seo/GetSeoData). The fallback is applied right away, so the previous page's
   * tags never linger, and stays in place when the CMS has no entry for this page.
   */
  applyCmsSeo(pageId: number, pageType: number, fallback: ISeoConfig): void {
    this.setPageMeta(fallback);
    this.getSeoData(pageId, pageType).subscribe({
      next: (resp) => {
        const api = resp?.success && Array.isArray(resp.data) ? resp.data[0] : null;
        if (api) this.setPageMeta(this.withFallback(this.mapApiToConfig(api), fallback));
      },
      error: (err) => console.error('[SEO] CMS SEO load failed:', err),
    });
  }

  /** SEO from the inventory API (products, categories); same fallback behaviour as applyCmsSeo */
  applyInventorySeo(pageId: number, pageType: number, fallback: ISeoConfig): void {
    this.setPageMeta(fallback);
    this.getSeoDataFromInventory(pageId, pageType).subscribe({
      next: (resp) => {
        if (resp?.success && resp.data) this.setPageMeta(this.withFallback(this.mapApiToConfig(resp.data), fallback));
      },
      error: (err) => console.error('[SEO] Inventory SEO load failed:', err),
    });
  }

  /** Pages that shouldn't be indexed (cart, checkout, account, auth, ...): title only + noindex */
  setPrivatePage(page: string): void {
    this.setPageMeta({ title: this.pageTitle(page), description: DEFAULT_DESCRIPTION, robots: 'noindex,nofollow' });
  }

  setPageMeta(config: ISeoConfig) {
    this.title.setTitle(`${config.title}`);
    this.meta.updateTag({ name: 'description', content: config.description });
    if (config.keywords) {
      this.meta.updateTag({ name: 'keywords', content: config.keywords });
    } else {
      this.meta.removeTag("name='keywords'");
    }
    if (config.image) {
      this.meta.updateTag({ property: 'og:image', content: config.image });
    } else {
      this.meta.removeTag("property='og:image'");
    }
    this.meta.updateTag({ property: 'og:title', content: config.title });
    this.meta.updateTag({ property: 'og:description', content: config.description });
    this.meta.updateTag({ property: 'og:type', content: config.type ?? 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: SITE_NAME });
    this.meta.updateTag({ name: 'robots', content: config.robots ?? 'index,follow' });
    this.setCanonical(config.canonicalPath);
  }

  /** Canonical URL + og:url for the current page (path only: query strings and fragments are dropped) */
  private setCanonical(canonicalPath?: string): void {
    const path = (canonicalPath || this.document?.location?.pathname || '/').replace(/\/+$/, '') || '/';
    const url = `${this.getCurrentOrigin()}${path}`;

    let link = this.document.head.querySelector<HTMLLinkElement>("link[rel='canonical']");
    if (!link) {
      link = this.document.createElement('link');
      link.setAttribute('rel', 'canonical');
      this.document.head.appendChild(link);
    }
    link.setAttribute('href', url);
    this.meta.updateTag({ property: 'og:url', content: url });
  }

  mapApiToConfig(api: any): ISeoConfig {
    // CMS values may carry stray leading/trailing spaces
    return {
      title: (api.seoTitle ?? '').trim(),
      description: (api.seoDescription ?? '').trim(),
      keywords: (api.keyword ?? '').trim(),
      image: '',
      robots: api.robot ? 'index,follow' : 'noindex,nofollow',
    };
  }

  /** Empty CMS fields fall back to the page's own values */
  private withFallback(config: ISeoConfig, fallback: ISeoConfig): ISeoConfig {
    return {
      ...config,
      title: config.title || fallback.title,
      description: config.description || fallback.description,
      keywords: config.keywords || fallback.keywords,
      image: config.image || fallback.image,
      type: fallback.type,
      canonicalPath: fallback.canonicalPath,
    };
  }

  /** Plain-text summary for meta descriptions (HTML stripped, ~160 characters) */
  summary(html: string | null | undefined, max = 160): string {
    const text = this.stripHtml(html ?? '');
    return text.length > max ? `${text.slice(0, max - 1).replace(/\s+\S*$/, '')}…` : text;
  }



  private setJsonLd(id: string, schema: any): void {
    if (!schema) return;

    let script = this.document.getElementById(id) as HTMLScriptElement | null;

    if (!script) {
      script = this.document.createElement('script');
      script!.id = id;
      script!.type = 'application/ld+json';
      this.document.head.appendChild(script);
    }

    script!.textContent = JSON.stringify(schema);
  }

  addProductSchema(product: any): void {
    if (!product) return;

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: product.productVariantName,
      image: product?.productGalleryItems?.length
        ? this.InventoryImagePrefix + product.productGalleryItems[0].imagePath
        : '',
      // description: this.stripHtml(product.productDescription),
      sku: product.sku,
      brand: {
        '@type': 'Brand',
        name: product.brandName,
      },
      offers: {
        '@type': 'Offer',
        priceCurrency: 'BDT',
        price: product.basePrice,
        availability: product.isInStock
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock',
      },
    };

    this.setJsonLd('product-schema', schema);
  }

  /**
   * BreadcrumbList JSON-LD: Home > categories (root → leaf) > current page.
   * `pagePath` (e.g. `/product/<slug>`) gives the last item its own URL.
   */
  addBreadcrumbSchema(productName: string, categoryHierarchy: any[] = [], pagePath?: string): void {
    if (!productName) return;

    const breadcrumbs: any[] = [];
    const origin = this.getCurrentOrigin();

    // Home (every ListItem needs a name)
    breadcrumbs.push({
      name: 'Home',
      url: `${origin}/`,
    });

    // Categories
    categoryHierarchy?.forEach((cat) => {
      if (!cat?.name || !cat?.slug) return;
      breadcrumbs.push({
        name: cat.name,
        url: `${origin}/category/${cat.slug}`,
      });
    });

    // Current page
    breadcrumbs.push({ name: productName, ...(pagePath ? { url: `${origin}${pagePath}` } : {}) });

    const schema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbs.map((bc, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: bc.name,
        ...(bc.url ? { item: bc.url } : {}),
      })),
    };

    this.setJsonLd('breadcrumb-schema', schema);
  }

  stripHtml(html: string): string {
    if (!html) return '';

    // remove tags (as spaces, so a heading and the next paragraph don't run together)
    const withoutTags = html.replace(/<[^>]*>/g, ' ');

    // decode HTML entities
    const textarea = this.document.createElement('textarea');
    textarea.innerHTML = withoutTags;

    return textarea.value.replace(/\s+/g, ' ').trim();
  }

}
